import { useIntl } from 'react-intl';

import {
	AdminIcon,
	AnalyticsIcon,
	AssetsIcon,
	BitbucketIcon,
	ChatIcon,
	CompassIcon,
	ConfluenceIcon,
	CustomerServiceManagementIcon,
	DxIcon,
	FocusIcon,
	GoalsIcon,
	HomeIcon,
	JiraIcon,
	JiraProductDiscoveryIcon,
	JiraServiceManagementIcon,
	LoomIcon,
	OpsgenieIcon,
	ProjectsIcon,
	RovoIcon,
	SearchIcon,
	StudioIcon,
	TalentIcon,
	TeamsIcon,
	TrelloIcon,
} from '@atlaskit/logo';

import messages from './messages';
import type { ProductId, UseAppSelector } from './types';

/*
 * This hook returns the icon & a localised text for the product name.
 * Note: Only platform apps are localised - https://hello.atlassian.net/wiki/spaces/i18n/pages/5012571632/Localization+proposal+for+Appification
 */
export const useAppSelector: UseAppSelector = (productId: ProductId | string) => {
	const { formatMessage } = useIntl();

	switch (productId) {
		case 'ADMIN':
			return {
				icon: AdminIcon,
				text: formatMessage(messages.admin),
			};
		case 'ANALYTICS':
			return {
				icon: AnalyticsIcon,
				text: formatMessage(messages.analytics),
			};
		case 'ASSETS':
			return {
				icon: AssetsIcon,
				text: formatMessage(messages.assets),
			};
		case 'BITBUCKET':
			return {
				icon: BitbucketIcon,
				text: 'Bitbucket',
			};
		case 'CHAT':
			return {
				icon: ChatIcon,
				text: formatMessage(messages.chat),
			};
		case 'CSM':
			return {
				icon: CustomerServiceManagementIcon,
				text: 'Customer Service Management',
			};
		case 'COMPASS':
			return {
				icon: CompassIcon,
				text: 'Compass',
			};
		case 'CONFLUENCE':
			return {
				icon: ConfluenceIcon,
				text: 'Confluence',
			};
		case 'DX':
			return {
				icon: DxIcon,
				text: 'DX',
			};
		case 'FOCUS':
			return {
				icon: FocusIcon,
				text: 'Focus',
			};
		case 'GOALS':
			return {
				icon: GoalsIcon,
				text: formatMessage(messages.goals),
			};
		case 'HOME':
			return {
				icon: HomeIcon,
				text: formatMessage(messages.home),
			};
		case 'JIRA':
			return {
				icon: JiraIcon,
				text: 'Jira',
			};
		case 'JPD':
			return {
				icon: JiraProductDiscoveryIcon,
				text: 'Jira Product Discovery',
			};
		case 'JSM':
			return {
				icon: JiraServiceManagementIcon,
				text: 'Jira Service Management',
			};
		case 'LOOM':
			return {
				icon: LoomIcon,
				text: 'Loom',
			};
		case 'OPSGENIE':
			return {
				icon: OpsgenieIcon,
				text: 'Opsgenie',
			};
		case 'PROJECTS':
			return {
				icon: ProjectsIcon,
				text: formatMessage(messages.projects),
			};
		case 'ROVO':
			return {
				icon: RovoIcon,
				text: formatMessage(messages.rovo),
			};
		case 'SEARCH':
			return {
				icon: SearchIcon,
				text: formatMessage(messages.search),
			};
		case 'STUDIO':
			return {
				icon: StudioIcon,
				text: formatMessage(messages.studio),
			};
		case 'TALENT':
			return {
				icon: TalentIcon,
				text: 'Talent',
			};
		case 'TEAMS':
			return {
				icon: TeamsIcon,
				text: formatMessage(messages.teams),
			};
		case 'TRELLO':
			return {
				icon: TrelloIcon,
				text: 'Trello',
			};
		default:
			return undefined;
	}
};
